import type { ReactNode } from 'react'
import { Explicacao } from '../../../components/Explicacao'
import { Link } from 'react-router-dom'
import { EtiquetaDeOrigem, EtiquetaDePrioridade, EtiquetaDeRisco, EtiquetaDeStatus } from '../../../components/Etiqueta'
import { IconeCadeado, IconeImprimir } from '../../../components/Icones'
import { ListaDeDados, type Dado } from '../../../components/ListaDeDados'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import type { DetalheGmud } from '../../../types/gmud'
import { AvisoDeHistoricoMigrado } from './AvisoDeHistoricoMigrado'

/**
 * A identificação da solicitação, fixa no topo — fora das abas e acima delas.
 *
 * Fora das abas de propósito: código, estado e risco são o que responde "de que registro
 * estamos falando", e essa resposta não pode desaparecer porque a pessoa foi ler o histórico.
 * Trocar de aba é mudar o assunto, nunca perder o cabeçalho.
 *
 * Aqui ficam os dois avisos que mudam o que se pode fazer com o registro. O primeiro é o
 * bloqueio para produção. O segundo é a marca de histórico importado, que é permanente e não
 * se dispensa: sem ela, uma das 1.027 solicitações trazidas do sistema anterior se lê como
 * registro deste processo — e nenhuma delas passou por comitê.
 *
 * @param props a solicitação
 * @returns o cabeçalho de identificação
 */
export function CabecalhoDaGmud({ gmud }: { gmud: DetalheGmud }) {
  const identificacao: Dado[] = [
    { rotulo: 'Chamado no e-Niterói', valor: gmud.chamadoNumero },
    { rotulo: 'Sistema afetado', valor: gmud.sistema },
    { rotulo: 'Quem abriu', valor: gmud.solicitante },
  ]

  return (
    <header className="cartao space-y-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0 space-y-3">
          <h1 className="titulo-da-pagina">{gmud.codigo}</h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Marca rotulo="Estado">
              <EstadoDaGmud gmud={gmud} />
            </Marca>
            <Marca rotulo="Prioridade">
              <EtiquetaDePrioridade prioridade={gmud.prioridade} />
            </Marca>
            <Marca rotulo={TERMOS.risco.rotulo}>
              <EtiquetaDeRisco risco={gmud.riscoNivel} />
            </Marca>
          </div>
        </div>
        <LinkDoRelatorio id={gmud.id} />
      </div>

      <ListaDeDados dados={identificacao} />

      {gmud.bloqueadoParaDeploy && <AvisoDeBloqueio />}
      {gmud.origem === 'MIGRADO_V1' && <AvisoDeHistoricoMigrado gmud={gmud} />}
    </header>
  )
}

/**
 * Etiqueta com o nome do que ela mede.
 *
 * "Alta" e "Moderado" lado a lado não dizem qual é a prioridade e qual é o risco — quem é de TI
 * adivinha pela cor, o fiscal de contrato não tem por que adivinhar.
 *
 * @param props o nome da medida e a etiqueta
 * @returns o par rótulo/etiqueta
 */
function Marca({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-xs font-medium text-slate-500">{rotulo}</span>
      {children}
    </span>
  )
}

/**
 * O estado, ou a procedência quando o registro é importado.
 *
 * Em registro do processo novo, o estado. Em registro importado, a PROCEDÊNCIA — ele não está em
 * etapa nenhuma do ANEXO XXI. A etiqueta de estado volta a aparecer se um importado estiver fora
 * de `MIGRADO_V1`: hoje todos estão, mas isso é coincidência da importação, não definição —
 * origem é fixada e não muda, estado é etapa e muda.
 *
 * @param props a solicitação
 * @returns a etiqueta de estado ou a de procedência
 */
function EstadoDaGmud({ gmud }: { gmud: DetalheGmud }) {
  if (gmud.origem !== 'MIGRADO_V1') {
    return <EtiquetaDeStatus status={gmud.status} rotulo={gmud.statusRotulo} />
  }
  return (
    <span className="flex flex-wrap items-center gap-2">
      <EtiquetaDeOrigem origem={gmud.origem} />
      {gmud.status !== 'MIGRADO_V1' && <EtiquetaDeStatus status={gmud.status} rotulo={gmud.statusRotulo} />}
    </span>
  )
}

/** O bloqueio para produção, escrito por extenso — no cabeçalho há espaço para a frase inteira. */
function AvisoDeBloqueio() {
  return (
    <p className="flex gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <IconeCadeado className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
      <span className="max-w-prose">
        <strong className="font-semibold">{TERMOS.bloqueadoParaDeploy.rotulo}:</strong>{' '}
        {TERMOS.bloqueadoParaDeploy.ajuda}
      </span>
    </p>
  )
}

/**
 * O caminho para o relatório impresso.
 *
 * É um link e não um botão, com a aparência de botão secundário: o destino é um endereço, e
 * transformá-lo em `<button>` (ou embrulhar um botão num link) tiraria de quem usa o navegador o
 * "abrir em nova aba", o "copiar endereço" e o clique do meio. O aviso de que abre em outra aba
 * fica para o leitor de tela, que não vê a mudança de contexto acontecer.
 *
 * @param props identificador da solicitação
 * @returns o link do relatório
 */
function LinkDoRelatorio({ id }: { id: number }) {
  return (
    <Link
      to={`/gmuds/${id}/relatorio`}
      target="_blank"
      rel="noreferrer"
      className={
        'inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm ' +
        'font-medium text-slate-800 shadow-sm transition-colors hover:border-slate-400 hover:bg-slate-50 ' +
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-niteroi-600'
      }
    >
      <IconeImprimir className="h-4 w-4" />
      {TEXTOS.relatorio.botao} ({TERMOS.rdm.sigla})
      <Explicacao termo="rdm" className="h-3.5 w-3.5" />
      <span className="sr-only">(abre em outra aba)</span>
    </Link>
  )
}
